import type { AnnualBehaviorNote } from '../types/domain';
import {
  ANNUAL_WEEKDAY_KEYS,
  buildAnnualPeriodLabels,
  parseAnnualCellKey,
  type AnnualClassEntry,
} from './annualTimetable';
import { mondayIsoForDateInLocal, dateToWeekdayKey } from './dashboardDateContext';
import { parseISODate } from './date';
import type { ProgramProgressRow } from './programProgressRows';

export type BehaviorMemoRecordKind = 'behavior';

export type BehaviorMemoRowInput = {
  key: string;
  termNumber: 1 | 2;
  sortKey: string;
  hasProgramSchedule: boolean;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  programLabel: string;
  programDotKind: 'fixed' | 'project' | 'track' | 'none';
  timetableText: string;
  unitLabel: string;
  learningLabel: string;
  recordKind: BehaviorMemoRecordKind;
  kindLabel: string;
  note: string;
  recordMemberGroupId: string | null;
};

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;

function shortDateLabel(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

/** 연간진도표 행동특성 → 기록 모아보기 행 */
export function buildBehaviorNoteMemoRows(params: {
  notes: AnnualBehaviorNote[];
  selectedEntry: AnnualClassEntry | null;
  rowsTerm1: ProgramProgressRow[];
  rowsTerm2: ProgramProgressRow[];
  selectedRecordGroupFilterId?: string;
  allRecordGroupsFilterId?: string;
  generalRecordGroupsFilterId?: string;
}): BehaviorMemoRowInput[] {
  const { notes, selectedEntry, rowsTerm1, rowsTerm2 } = params;
  const allGroupsId = params.allRecordGroupsFilterId ?? '__all_record_groups__';
  const generalGroupsId = params.generalRecordGroupsFilterId ?? '__general_record_groups__';
  const groupFilter = params.selectedRecordGroupFilterId ?? allGroupsId;
  const periodLabels = selectedEntry ? buildAnnualPeriodLabels(selectedEntry.sectionRows) : [];

  const scheduleByTerm = {
    1: new Map(rowsTerm1.map((r) => [r.annualCellKey, r] as const)),
    2: new Map(rowsTerm2.map((r) => [r.annualCellKey, r] as const)),
  } as const;

  const out: BehaviorMemoRowInput[] = [];

  for (const note of notes) {
    const text = (note.behavior_note ?? '').trim();
    if (!text) continue;

    const groupId = (note.record_member_group_id ?? '').trim();
    if (groupFilter !== allGroupsId) {
      if (groupFilter === generalGroupsId) {
        if (groupId) continue;
      } else if (groupId !== groupFilter) {
        continue;
      }
    }

    const termNumber = note.term_number === 2 ? 2 : 1;
    const dateIso = (note.lesson_date ?? '').trim();
    if (!dateIso) continue;
    const date = parseISODate(dateIso);
    if (Number.isNaN(date.getTime())) continue;

    const annualKey = (note.source_annual_cell_key_snapshot ?? '').trim();
    const scheduleRow = annualKey ? scheduleByTerm[termNumber].get(annualKey) : undefined;
    const coords = annualKey ? parseAnnualCellKey(annualKey) : null;

    const mondayIso =
      scheduleRow?.mondayIso ??
      coords?.mondayIso ??
      mondayIsoForDateInLocal(date);
    const weekdayKey =
      scheduleRow?.weekdayKey ?? coords?.weekdayKey ?? dateToWeekdayKey(date);
    const weekdayLabel =
      scheduleRow?.weekdayLabel ??
      (weekdayKey != null
        ? (ANNUAL_WEEKDAY_KEYS.find((w) => w.key === weekdayKey)?.label ?? weekdayKey)
        : WEEKDAY_KO[date.getDay()] ?? '—');
    const slotIndex = scheduleRow?.slotIndex ?? coords?.slotIndex ?? Math.max(0, note.day_row_order ?? 0);
    const periodLabel =
      (scheduleRow?.periodLabel || '').trim() ||
      (note.period_label_snapshot ?? '').trim() ||
      periodLabels[slotIndex] ||
      `${slotIndex + 1}타임`;

    out.push({
      key: `behavior:${note.id || `${termNumber}|${dateIso}|${note.day_row_order}`}`,
      termNumber,
      sortKey: `${termNumber}|${dateIso}|${String(slotIndex).padStart(3, '0')}|behavior|${note.id}`,
      hasProgramSchedule: true,
      dateLabel: scheduleRow?.dateLabel || shortDateLabel(dateIso),
      weekdayLabel,
      periodLabel,
      programLabel: '—',
      programDotKind: 'none',
      timetableText: scheduleRow?.timetableText || (note.timetable_text_snapshot ?? '').trim(),
      unitLabel: '',
      learningLabel: '',
      recordKind: 'behavior',
      kindLabel: '행동특성',
      note: text,
      recordMemberGroupId: groupId || null,
    });
  }

  return out;
}
